import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminRefundQueueTab = ({ formatDate }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState('pending'); // 'pending' | 'all' | 'resolved'

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/refund-queue?limit=100`);
      const data = await res.json();
      setJobs(data.jobs || []);
    } catch {
      toast.error('Failed to load refund queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resolve = async (jobId, status) => {
    setBusyId(jobId);
    try {
      const res = await fetch(`${API_URL}/api/admin/refund-queue/${jobId}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, notes: '' }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(status === 'resolved' ? 'Marked refunded' : 'Marked skipped');
      await load();
    } catch {
      toast.error('Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const retry = async (jobId) => {
    setBusyId(jobId);
    try {
      const res = await fetch(`${API_URL}/api/admin/refund-queue/${jobId}/retry`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Retry queued — refresh in a minute to see result');
      await load();
    } catch {
      toast.error('Retry failed');
    } finally {
      setBusyId(null);
    }
  };

  const razorpayRefund = async (job) => {
    if (!job.payment_id || String(job.payment_id).startsWith('free_')) {
      toast.error('No Razorpay payment on this job (free / coupon claim).');
      return;
    }
    const notes = window.prompt(
      `Issue a Razorpay refund for payment ${job.payment_id}?\nOptionally add internal notes for the audit trail:`,
      'Customer complaint refund',
    );
    if (notes === null) return; // cancelled
    setBusyId(job.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/refund-queue/${job.id}/razorpay-refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data.detail
            || `Razorpay refund failed (${res.status} ${res.statusText || ''}).`.trim()
        );
      }
      toast.success(`Refund issued: ${data.refund_id} (${data.razorpay_status})`);
      await load();
    } catch (e) {
      toast.error(e.message || 'Refund failed');
    } finally {
      setBusyId(null);
    }
  };

  const filtered = jobs.filter(j => {
    const s = j.refund_status || 'pending';
    if (filter === 'all') return true;
    if (filter === 'resolved') return s === 'resolved' || s === 'skipped';
    return s === 'pending';
  });

  const pendingCount = jobs.filter(j => (j.refund_status || 'pending') === 'pending').length;

  return (
    <Card className="border-gray-800 bg-gray-950" data-testid="admin-refund-queue-tab">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-gray-100 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
              Refund Queue
            </CardTitle>
            <div className="text-xs text-gray-400 mt-1">
              Paid jobs where PDF generation exhausted all retries. Refund the user, then mark resolved.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-black rounded-md border border-gray-800 overflow-hidden">
              {['pending', 'resolved', 'all'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  data-testid={`refund-filter-${f}`}
                  className={`px-3 py-1 text-xs uppercase tracking-wide ${
                    filter === f
                      ? 'bg-purple-700 text-white'
                      : 'text-gray-400 hover:text-gray-100'
                  }`}
                >
                  {f} {f === 'pending' && pendingCount > 0 ? `(${pendingCount})` : ''}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="refund-refresh-btn"
              className="border-gray-700 text-gray-300 hover:bg-gray-900"
            >
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && jobs.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
            Loading refund queue...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-gray-500 text-sm" data-testid="refund-empty-state">
            {filter === 'pending' ? 'No pending refunds — all clear.' : 'Nothing to show.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-800">
                  <th className="pb-2 pr-4">User</th>
                  <th className="pb-2 pr-4">Payment ID</th>
                  <th className="pb-2 pr-4">Archetype</th>
                  <th className="pb-2 pr-4">Attempts</th>
                  <th className="pb-2 pr-4">Failed at</th>
                  <th className="pb-2 pr-4">Error</th>
                  <th className="pb-2 pr-4">Status</th>
                  <th className="pb-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(job => (
                  <tr key={job.id} className="border-b border-gray-900" data-testid={`refund-row-${job.id}`}>
                    <td className="py-3 pr-4">
                      <div className="text-gray-200">{job?.user_data?.name || '—'}</div>
                      <div className="text-xs text-gray-500">{job?.user_data?.email || ''}</div>
                    </td>
                    <td className="py-3 pr-4">
                      <code className="text-xs text-gray-400">{job.payment_id || '—'}</code>
                    </td>
                    <td className="py-3 pr-4 text-gray-300">
                      {job.primary} + {job.secondary}
                    </td>
                    <td className="py-3 pr-4 text-gray-400">
                      {job.attempts || 0} / {job.max_attempts || 3}
                    </td>
                    <td className="py-3 pr-4 text-xs text-gray-400">
                      {formatDate ? formatDate(job.completed_at || job.updated_at) : (job.completed_at || job.updated_at)}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="text-xs text-red-400 max-w-xs truncate" title={job.error}>
                        {job.error || '—'}
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <Badge variant="outline" className={
                        (job.refund_status || 'pending') === 'resolved'
                          ? 'border-green-700 text-green-400'
                          : (job.refund_status === 'skipped')
                            ? 'border-gray-600 text-gray-400'
                            : 'border-yellow-700 text-yellow-400'
                      }>
                        {job.refund_status || 'pending'}
                      </Badge>
                    </td>
                    <td className="py-3">
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === job.id}
                          onClick={() => retry(job.id)}
                          data-testid={`refund-retry-${job.id}`}
                          className="border-gray-700 text-gray-300 hover:bg-gray-900 text-xs h-7"
                        >
                          Retry
                        </Button>
                        <Button
                          size="sm"
                          disabled={busyId === job.id || !job.payment_id || String(job.payment_id).startsWith('free_') || job.refund_status === 'resolved'}
                          onClick={() => razorpayRefund(job)}
                          data-testid={`refund-razorpay-${job.id}`}
                          className="bg-blue-700 hover:bg-blue-600 text-white text-xs h-7"
                          title="Issue refund via Razorpay API"
                        >
                          Razorpay Refund
                        </Button>
                        <Button
                          size="sm"
                          disabled={busyId === job.id || job.refund_status === 'resolved'}
                          onClick={() => resolve(job.id, 'resolved')}
                          data-testid={`refund-resolve-${job.id}`}
                          className="bg-green-700 hover:bg-green-600 text-white text-xs h-7"
                          title="Mark resolved without calling Razorpay (manual dashboard refund)"
                        >
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Mark Refunded
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === job.id || job.refund_status === 'skipped'}
                          onClick={() => resolve(job.id, 'skipped')}
                          data-testid={`refund-skip-${job.id}`}
                          className="border-gray-700 text-gray-300 hover:bg-gray-900 text-xs h-7"
                        >
                          <XCircle className="h-3 w-3 mr-1" /> Skip
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
